import { CampaignAPI, UserAPI } from "generated-api"
import { normalizeSync } from "normalize-diacritics"
import { checkIfSessionIsOrWillExpired, getUserId } from "user/tools/common"
import { ITagRepository, IUserTag } from "./model"

export const tagRPO: ITagRepository = {
  getAllUserTags(userId: number): Promise<IUserTag[]> {
    return new Promise<IUserTag[]>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        UserAPI.getApiUserTagList(userId)
          .then(response =>
            resolve(
              response.map(t => ({
                ...t,
                normalizedName: normalizeSync(t.name).toLowerCase()
              }))
            )
          )
          .catch((error: any) => {
            console.error("Server error while getting all user tags", error)
            reject("tag.user.get.all.error.server")
          })
      } catch (ex) {
        console.error("Server error while getting all user tags", ex)
        reject("tag.user.get.all.error.unexpected")
      }
    })
  },
  getAllUserTagsForCampaign(campaignId: number): Promise<IUserTag[]> {
    return new Promise<IUserTag[]>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        const userId = getUserId()
        CampaignAPI.getApiCampaignUsertagList(campaignId)
          .then(response =>
            resolve(
              response.map(t => ({
                id: t.user_tag_id,
                user_id: userId,
                name: t.user_tag.name,
                normalizedName: normalizeSync(t.user_tag.name).toLowerCase()
              }))
            )
          )
          .catch((error: any) => {
            console.error("Server error while getting all campaign user tags", error)
            reject("tag.user.campaign.get.all.error.server")
          })
      } catch (ex) {
        console.error("Server error while getting all campaign user tags", ex)
        reject("tag.user.campaign.get.all.error.unexpected")
      }
    })
  },
  createUserTag(userId: number, tag: string): Promise<IUserTag> {
    return new Promise<IUserTag>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        UserAPI.postApiUserTag({ user_id: userId, name: tag })
          .then(response =>
            resolve({
              ...response,
              normalizedName: normalizeSync(response.name).toLowerCase()
            })
          )
          .catch((error: any) => {
            console.error("Server error while creating new user tag", error)
            reject("tag.user.create.error.server")
          })
      } catch (ex) {
        console.error("Server error while creating new user tag", ex)
        reject("tag.user.create.error.unexpected")
      }
    })
  },
  deleteUserTag(userId: number, userTagId: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        UserAPI.deleteApiUserTag(userId, userTagId)
          .then(() => resolve())
          .catch((error: any) => {
            console.error("Server error while deleting user tag", error)
            reject("tag.user.delete.error.server")
          })
      } catch (ex) {
        console.error("Server error while deleting user tag", ex)
        reject("tag.user.delete.error.unexpected")
      }
    })
  },
  associateUserTagToCampaign(campaignId: number, userTag: IUserTag): Promise<IUserTag> {
    return new Promise<IUserTag>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        CampaignAPI.postApiCampaignUsertag({
          campaign_id: campaignId,
          user_tag_id: userTag.id
        })
          .then(response =>
            resolve({
              ...userTag,
              ...response
            })
          )
          .catch((error: any) => {
            console.error("Server error while associating user tag to campaign", error)
            reject("tag.user.campaign.associate.error.server")
          })
      } catch (ex) {
        console.error("Server error while associating user tag to campaign", ex)
        reject("tag.user.campaign.associate.error.unexpected")
      }
    })
  },
  deassociateUserTagToCampaign(campaignId: number, tagAssociationId: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        CampaignAPI.deleteApiCampaignUsertag(campaignId, tagAssociationId)
          .then(() => resolve())
          .catch((error: any) => {
            console.error("Server error while deassociating user tag to campaign", error)
            reject("tag.user.campaign.deassociate.error.server")
          })
      } catch (ex) {
        console.error("Server error while deassociating user tag to campaign", ex)
        reject("tag.user.campaign.deassociate.error.unexpected")
      }
    })
  }
}
